import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import { client } from '../../client';
import type { AssetClass, ImageView } from '../../generated';

/*
 * Hand-written stand-in for GET /v1/workspaces/{workspace_id}/benchmarks/{benchmark_id}/images. The route is live
 * behind the API's `custom-benchmarks` flag and answers with the generated `ImageView`, but the spec the generator
 * reads does not list the route yet. When it does, point the fetcher at the generated SDK function and keep
 * `toCustomBenchmarkImage` as the one place the response is mapped. Tests answer this path with
 * `tests/mocks/benchmark-handlers.ts`.
 */

const LIST_BENCHMARK_IMAGES_URL = '/v1/workspaces/{workspace_id}/benchmarks/{benchmark_id}/images';

export type BenchmarkImageSortBy = 'created_at' | 'name';

export type BenchmarkImageSortDirection = 'asc' | 'desc';

/**
 * The benchmark's score for one image under a product category, returned on each image when the listing is asked
 * for that category. Mirrors the API's `PairScoreView`; the generated types gain it with the next regeneration.
 */
export interface BenchmarkPairScoreResponse {
  benchmark_id: string;
  retailer: string | null;
  product_category_id: string;
  model: 'clip' | 'effnet';
  raw_score: number | null;
  vizit_score: number | null;
  /** `false` when not scored yet, the pool is empty, or the pair was never requested. */
  scored: boolean;
  /** `null` when the pair was never requested. */
  scoring_status: 'pending' | 'ready' | 'failed' | null;
}

/** A benchmark image as the listing returns it: the image, plus its pair score when a category was asked for. */
export type BenchmarkImageResponse = ImageView & { pair_score?: BenchmarkPairScoreResponse | null };

/** An image of a custom benchmark, mapped for the UI. */
export interface CustomBenchmarkImage {
  id: string;
  /** The uploaded file name. */
  name: string;
  /** Small rendition for grid tiles. It 404s until the image is resized, which happens on its first scoring. */
  thumbnailUrl: string | null;
  /** Large rendition for the preview. It 404s until the image is resized, like `thumbnailUrl`. */
  previewUrl: string | null;
  /** The full-size original, the fallback while the renditions do not exist yet. */
  originalUrl: string | null;
  /**
   * The benchmark's score for this image under the requested product category. `null` without a category, and
   * while the pair is not scored: the image's own `vizit_score` scores it against its own category instead, so it
   * is never used here.
   */
  vizitScore: number | null;
  /** Where scoring the image under the requested category stands; `null` without a category. */
  scoringStatus: BenchmarkPairScoreResponse['scoring_status'];
  classification: AssetClass | null;
  /** When the image last changed, which anchors how long a scoring may stay pending. */
  updatedAt: string | null;
}

export function toCustomBenchmarkImage(image: BenchmarkImageResponse): CustomBenchmarkImage {
  // All three URLs are signed locally, without checking S3, so they are never a sign the file has arrived.
  const originalUrl = image.url ?? null;
  const pairScore = image.pair_score ?? null;

  return {
    id: image.id,
    name: image.name,
    thumbnailUrl: image.url_360 ?? originalUrl,
    previewUrl: image.url_760 ?? originalUrl,
    originalUrl,
    vizitScore: pairScore?.scored ? pairScore.vizit_score : null,
    scoringStatus: pairScore?.scoring_status ?? null,
    classification: image.classification ?? null,
    updatedAt: image.updated_at ?? image.created_at,
  };
}

export interface ListBenchmarkImagesQueryVariables {
  workspace_id: string;
  benchmark_id: string;
  /** Adds each image's `pair_score` for this category. The API then caps `limit` at 100. */
  product_category_id?: string;
  limit?: number;
  offset?: number;
  sort_by?: BenchmarkImageSortBy;
  sort_direction?: BenchmarkImageSortDirection;
}

export interface ListBenchmarkImagesQuery {
  images: CustomBenchmarkImage[];
  /** Every image in the benchmark, from the `Vizit-Total-Count` header. */
  totalCount: number;
}

async function listBenchmarkImages({ workspace_id, benchmark_id, ...query }: ListBenchmarkImagesQueryVariables) {
  const { data, response } = await client.get<{ 200: BenchmarkImageResponse[] }, unknown, true>({
    url: LIST_BENCHMARK_IMAGES_URL,
    path: { workspace_id, benchmark_id },
    query,
    throwOnError: true,
  });
  const totalCountHeader = response.headers.get('vizit-total-count');

  return {
    images: (data ?? []).map(toCustomBenchmarkImage),
    totalCount: totalCountHeader ? Number.parseInt(totalCountHeader, 10) : (data ?? []).length,
  };
}

export const useListBenchmarkImagesQuery = <TData = ListBenchmarkImagesQuery, TError = unknown>(
  variables: ListBenchmarkImagesQueryVariables,
  options?: Omit<UseQueryOptions<ListBenchmarkImagesQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<ListBenchmarkImagesQuery, TError, TData>({
    queryKey: useListBenchmarkImagesQuery.getKey(variables),
    queryFn: () => listBenchmarkImages(variables),
    ...options,
  });
};

useListBenchmarkImagesQuery.getKey = (variables: ListBenchmarkImagesQueryVariables) => [
  'ListBenchmarkImages',
  variables,
];

useListBenchmarkImagesQuery.fetcher = (variables: ListBenchmarkImagesQueryVariables) => () =>
  listBenchmarkImages(variables);
